import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { MANIFESTO } from "@/lib/content";

const LAKE_IMG =
  "https://images.unsplash.com/photo-1781124897128-7e3ea12a8bc1?q=80&w=2000&auto=format&fit=crop";

const MARQUEE_ITEMS = ["Personal tax", "Corporate tax", "Accounting", "Small business services", "CRA representation", "Business advisory", "Estates & trusts"];

export function Marquee() {
  return (
    <div className="overflow-hidden border-y border-[#102036]/10 bg-[#FFFFFF] py-6" aria-hidden="true" data-testid="marquee">
      <div className="marquee-track">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0 items-center">
            {MARQUEE_ITEMS.map((item) => (
              <span key={`${copy}-${item}`} className="flex items-center whitespace-nowrap">
                <span className="px-8 font-display text-3xl italic text-[#102036]/70 sm:text-4xl">{item}</span>
                <span className="h-2 w-2 rounded-full bg-[#3682AE]/50" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="about" className="bg-[#FFFFFF] py-24 lg:py-32" data-testid="about-section">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>About Ford &amp; Associates</Eyebrow>
                <h2 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
                  Accounting &amp; tax solutions, tailored to you.
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5568]">
                  We provide expert accounting and tax solutions tailored to individuals and businesses — and we
                  simplify taxation, compliance and financial decision-making along the way.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 overflow-hidden rounded-lg">
                  <img
                    src="https://images.unsplash.com/photo-1758518729922-3a9e7b62b4c9?q=80&w=1200&auto=format&fit=crop"
                    alt="Advisor at work in a modern Newmarket office"
                    className="h-72 w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            {MANIFESTO.map((c, i) => (
              <Reveal key={c.num} delay={0.05 * i}>
                <div className="group border-t border-[#102036]/12 py-8 transition-colors first:border-t-0 first:pt-0 sm:py-10" data-testid={`manifesto-chapter-${c.num}`}>
                  <div className="flex gap-6 sm:gap-10">
                    <span className="font-display text-lg italic text-[#102036]/50">{c.num}</span>
                    <div>
                      <h3 className="font-display text-3xl font-medium text-[#102036] transition-transform duration-300 group-hover:translate-x-1.5 sm:text-4xl">
                        {c.title}
                      </h3>
                      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#4A5568]">{c.text}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <div ref={ref} className="relative mt-20 h-[420px] overflow-hidden lg:h-[520px]" data-testid="about-parallax-band">
        <motion.img
          style={{ y }}
          src={LAKE_IMG}
          alt="Morning mist over a Canadian lake"
          className="absolute inset-0 h-[120%] w-full -translate-y-[10%] object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-[#102036]/45" aria-hidden="true" />
        <div className="relative mx-auto flex h-full max-w-[1320px] items-center px-5 sm:px-8">
          <Reveal>
            <blockquote className="max-w-3xl font-display text-3xl font-medium leading-snug text-[#FFFFFF] sm:text-4xl lg:text-5xl">
              “We simplify taxation, compliance and financial decision-making — so you can stay focused on what
              matters most.”
            </blockquote>
            <cite className="mt-6 block text-[11px] font-bold uppercase not-italic tracking-[0.28em] text-[#FFFFFF]/70">
              Ford &amp; Associates
            </cite>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <div className="rounded-lg border border-[#102036]/10 bg-white p-10 sm:p-14" data-testid="trust-band">
            <Eyebrow>Why clients trust us</Eyebrow>
            <div className="mt-8 grid gap-10 md:grid-cols-3">
              {[
                ["Unhurried, personal meetings", "Clients tell us they never feel rushed — questions answered patiently, returns often completed while they wait."],
                ["Advice beyond the return", "From GST/HST to executive advisory, guidance is grounded in your full financial picture, not just this filing."],
                ["Secure, modern technology", "Encrypted client portals, digital document exchange and e-signatures — your information stays protected."],
              ].map(([title, text]) => (
                <div key={title}>
                  <h3 className="font-display text-2xl font-semibold text-[#102036]">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A5568]">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
